import Popover from "common/Popover/Popover";
import NotificationsList from "features/notifications/NotificationsList";
import Title from "common/Typography/Title";
import styled, { useTheme } from "styled-components";
import { Settings } from "lucide-react";
import { zIndexes } from "@shared/common/Themes/AppTheme";
import Link from "common/Typography/Link";
import useMedia from "utils/hooks/useMedia";
import { PropsWithChildren, useContext } from "react";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import SettingOutlined from "@ant-design/icons/SettingOutlined";
import { useHistory } from "react-router-dom";
import {
  IS_WEB_VIEW,
  MOBILE_SEARCH,
  NOTIFICATIONS_SETTINGS_PATH,
  SMALL_MOBILE,
} from "common/constants";
import IconActionButton from "common/Buttons/IconActionButton";
import Flex from "common/Boxes/Flex";
import { sharedLib } from "@shared/services/shared.service";
import Modal from "common/Modal/Modal";
import useMeasure from "utils/hooks/useMeasure";
import { clearBadgeCount } from "services/webview/webViewBridgeUtils.ts";
import { useIsSubUser } from "@shared/utils/hooks/misc.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";

interface NotificationsModalProps extends PropsWithChildren {
  onCloseModal: () => void;
  onOpenModal: () => void;
  numUnreadNotifications: number;
  visible: boolean;
}

const NotificationsModal = ({
  onCloseModal,
  onOpenModal,
  numUnreadNotifications,
  visible,
  children,
}: NotificationsModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const history = useHistory();
  const { analytics } = useContext(AnalyticsContext);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const isSmallMobile = useMedia(
    [`(max-width: ${SMALL_MOBILE})`],
    [true],
    false
  );
  const [measureTriggerRef, triggerSize] = useMeasure<HTMLDivElement>();
  const [markNotificationsRead] =
    sharedLib.sharedApi.notifications.useMarkNotificationsReadMutation();
  const isSubUser = useIsSubUser();
  const { isNewLook } = useTheme();

  const onMarkNotificationsRead = () => {
    markNotificationsRead();
    analytics?.notificationMarkAllAsRead({
      numberOfUnreadNotifications: numUnreadNotifications,
    });
    if (IS_WEB_VIEW) {
      clearBadgeCount().catch(() => {});
    }
  };

  const onSettingsIconClick = () => {
    onCloseModal();
    history.push(NOTIFICATIONS_SETTINGS_PATH);
  };

  const newLookTitleBox = (
    <NewLookTitleBox>
      <NewLookTitle>{t("labels.notifications")}</NewLookTitle>
      {!isSubUser && (
        <NewLookHeaderActions>
          <Link onClick={onMarkNotificationsRead}>
            {t("labels.mark_all_as_read")}
          </Link>
          <NewLookIconButton
            type="button"
            aria-label={t("user_info.settings_button")}
            title={t("user_info.settings_button")}
            onClick={onSettingsIconClick}
          >
            <Settings size={16} strokeWidth={1.75} />
          </NewLookIconButton>
        </NewLookHeaderActions>
      )}
    </NewLookTitleBox>
  );

  const content = isNewLook ? (
    <ContentWrapper onClick={(e: React.MouseEvent) => e.preventDefault()}>
      {newLookTitleBox}
      <NotificationsList closeModal={onCloseModal} />
    </ContentWrapper>
  ) : (
    <ContentWrapper onClick={(e: React.MouseEvent) => e.preventDefault()}>
      <TitleBox>
        <Flex>
          <Title level={3} style={{ marginRight: 8 }}>
            {t("labels.notifications")}
          </Title>
          {!isSubUser && (
            <IconActionButton
              icon={<SettingOutlined />}
              actionName="Settings"
              onClick={onSettingsIconClick}
              secondaryButton={true}
            />
          )}
        </Flex>
        {!isSubUser && (
          <Link onClick={onMarkNotificationsRead}>
            {t("labels.mark_all_as_read")}
          </Link>
        )}
      </TitleBox>
      <NotificationsList closeModal={onCloseModal} />
    </ContentWrapper>
  );

  const handleVisibleChange = (visible: boolean) => {
    if (visible) onOpenModal();
    else onCloseModal();
  };

  if (isMobile) {
    return (
      <>
        <div
          onClick={() => handleVisibleChange(!visible)}
          ref={measureTriggerRef}
        >
          {children}
        </div>
        <Modal
          open={visible}
          onCancel={onCloseModal}
          styles={{
            body: {
              maxHeight: 575,
              overflow: "auto",
              padding: 0,
            },
          }}
          style={{ top: triggerSize.bottom }}
          closeIcon={null}
        >
          {content}
        </Modal>
      </>
    );
  }

  return (
    <StyledPopover
      content={content}
      trigger={["click"]}
      open={visible}
      onOpenChange={handleVisibleChange}
      placement={isMobile ? undefined : "bottomRight"}
      zIndex={zIndexes.popover}
      styles={{
        root: {
          width: isSmallMobile ? "calc(100% - 24px)" : 400,
          maxWidth: "100%",
        },
        container: {
          padding: 0,
        },
      }}
      arrow={{ pointAtCenter: true }}
    >
      {children}
    </StyledPopover>
  );
};

export default NotificationsModal;

const ContentWrapper = styled.div`
  width: 100%;
`;
const TitleBox = styled.div`
  padding: 20px 16px;
  border-bottom: ${(p) => p.theme.borders.lightest};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
/** The popover's header, like a panel header: 52px, title left, hairline. */
const NewLookTitleBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 52px;
  padding: 10px 12px 10px 20px;
  flex-wrap: wrap;
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
const NewLookTitle = styled.h3`
  margin: 0;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const NewLookHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  && .ant-typography {
    ${newLookType.label};
    padding: 0 6px;
  }
`;
const NewLookIconButton = styled.button`
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: transparent;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  cursor: pointer;
  transition: background-color 120ms, color 120ms;
  &:hover {
    background: ${(p) => getNewLookTokens(p.theme).rowHover};
    color: ${(p) => getNewLookTokens(p.theme).ink};
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    box-shadow: 0 0 0 2px ${(p) => getNewLookTokens(p.theme).focus};
  }
`;
const StyledPopover = styled(Popover)`
  &.ant-popover {
    left: unset;
    inset: unset;
    right: 16px;
  }
`;
